import { pushDialog, setLocale } from ".";
import { fetchApi } from "../../config/fetchConfig";
import I18n from "../../I18n";
import dompurify from "dompurify";
import { logoutFromSSO, sanitizeErrorForLogging } from "../../utils/utils";
import { toNumericImportId } from "../../utils/importIdUtils";
import { getConfigVariable } from "../../config/config";
import { getIdTokenFromStorage, localStorageEnum } from "../../components/GlobalLayout/util";

const loginSuccess = (data) => ({
    type: "LOGIN_SUCCESS",
    payload: {
        data,
    },
});

const loginError = (data) => ({
    type: "LOGIN_ERROR",
    payload: {
        data,
    },
});
export const logoutSuccess = (auth) => ({
    payload: auth,
    type: "LOGOUT_SUCCESS",
});

export const updateUserSession = (data) => ({
    type: "UPDATE_USER_SESSION",
    payload: {
        data,
    },
});

export const login =
    (username, credential, authMode, fn = {}, auth = null, publicKeyUrl, locale = "en") =>
    (dispatch) => {
        const queriesBody = {
            query: `mutation($u:String!,$c:String!,$s:String,$pK:String){session(username:$u,credential:$c,auth_mode:$s,jwks_url:$pK){session}}`,
            variables: {
                u: username,
                c: credential,
                s: authMode,
                pK: publicKeyUrl,
            },
        };
        fetchApi("/graphql", queriesBody).then((res) =>
            res
                .json()
                .then((res) => {
                    const { data, errors } = res;

                    if (errors) {
                        if (fn.setAttempt) {
                            fn.setAttempt(true);
                        }
                        return dispatch(loginError(errors[0].message));
                    }

                    if (data.session) {
                        data.session.auth = auth;
                        dispatch(loginSuccess(data.session));
                        auth?.user?.id_token &&
                            window.localStorage.setItem(
                                localStorageEnum.SAFT_ID_TOKEN,
                                auth.user.id_token,
                            );
                        dispatch(setLocale(locale));
                    } else {
                        dispatch(
                            loginError(
                                "Invalid credentials. Make sure you are using your username and password.",
                            ),
                        );
                    }
                    if (fn.setAttempt) {
                        fn.setAttempt(true);
                    }
                })
                .catch(() =>
                    dispatch(loginError("Api error or Unknown error. Please try again later")),
                )
                .finally(() => {
                    if (fn.setAttempt) {
                        fn.setAttempt(true);
                    }
                }),
        );
    };

export const logout = (auth) => (dispatch, getState) => {
    const queriesBody = {
        query: `mutation{deleteSession{ok}}`,
    };
    fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
        .then((res) => res.json().catch(() => ({})))
        .then((res) => {
            const data = res.data || {};
            if (data?.deleteSession?.ok || res?.errors) {
                if (getConfigVariable("REACT_APP_AUTH_MODE") !== "LEGACY") {
                    logoutFromSSO(auth).then(() => {
                        dispatch(logoutSuccess(auth));
                    });
                } else {
                    dispatch(logoutSuccess(auth));
                }
            }
        })
        .catch(() => {
            if (getConfigVariable("REACT_APP_AUTH_MODE") !== "LEGACY") {
                logoutFromSSO(auth).then(() => {
                    dispatch(logoutSuccess(auth));
                });
            } else {
                dispatch(logoutSuccess(auth));
            }
        });
};

export const getFindingDetails =
    (projectId, fiscalYear, modulePrefix, ruleId, importId) => (dispatch, getState) => {
        const queriesBody = {
            query: `mutation($projectId:String,$fiscalYear:String,$modulePrefix:String,$ruleId:String,$importId:String){requestFindingDetailByRuleId(projectId:$projectId,fiscalYear:$fiscalYear,modulePrefix:$modulePrefix,ruleId:$ruleId,importId:$importId)}`,
            variables: {
                projectId: projectId,
                fiscalYear: fiscalYear,
                modulePrefix: modulePrefix,
                ruleId: ruleId,
                importId: importId,
            },
        };

        return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
            .then((res) => res.json().catch(() => ({})))
            .then((res) => {
                const errors = res?.errors;
                if (errors?.length) {
                    throw errors[0].message;
                }
                return true;
            });
    };

export const executeSelectedRules =
    ({ projectId, fiscalYear, importId, modulePrefix, rules }) =>
    (dispatch, getState) => {
        const queriesBody = {
            query: `mutation($projectId:String,$fiscalYear:String,$importId:String,$modulePrefix:String,$rules:[SelectedRuleInput!]!){requestSelectedRulesExecution(projectId:$projectId,fiscalYear:$fiscalYear,importId:$importId,modulePrefix:$modulePrefix,rules:$rules)}`,
            variables: {
                projectId,
                fiscalYear,
                importId,
                modulePrefix,
                rules,
            },
        };

        return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
            .then((res) => res.json().catch(() => ({})))
            .then((res) => {
                const errors = res?.errors;
                if (errors?.length) {
                    throw errors[0].message;
                }
                return true;
            });
    };

//RESET PASSWORD
const resetPasswordSuccess = (data) => ({
    type: "RESET_SUCCESS",
    payload: {
        data,
    },
});

const resetPasswordError = (msg) => ({
    type: "RESET_ERROR",
    payload: {
        msg: msg,
    },
});

const forgetPasswordMessage = (msg) => ({
    type: "FORGET_PASSWORD_MESSAGE",
    payload: {
        msg,
    },
});

export const forgetPassword = (email) => (dispatch) => {
    const queryBody = {
        query: `mutation($p:String!){forgetPassword(email:$p){success, message}}`,
        variables: {
            p: email,
        },
    };

    if (queryBody) {
        fetchApi("/graphql", queryBody)
            .then((res) => res.json())
            .then((res) => {
                const { data, errors } = res;
                if (errors) {
                    return dispatch(
                        forgetPasswordMessage({
                            success: false,
                            message: errors[0].message,
                        }),
                    );
                } else {
                    if ((data || {}).forgetPassword && (data || {}).forgetPassword.success) {
                        return dispatch(
                            forgetPasswordMessage({
                                success: true,
                                message: data.forgetPassword.message,
                            }),
                        );
                    }
                }
            })
            .catch(() =>
                dispatch(
                    forgetPasswordMessage({
                        success: false,
                        message: "Api error or Unknown error. Please try again later",
                    }),
                ),
            );
    }
};

export const resetPassword =
    (currentCredential, nextCredential, confirmCredential) => (dispatch, getState) => {
        if (nextCredential !== confirmCredential) {
            return dispatch(
                resetPasswordError('Your "new password" and "confirm password" does not match.'),
            );
        }

        const queriesBody = {
            query: `mutation($p:String!,$n:String!){reset_password(oldCredential:$p,newCredential:$n){success}}`,
            variables: {
                p: currentCredential,
                n: nextCredential,
            },
        };

        if (queriesBody) {
            fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user }).then(
                (res) =>
                    res
                        .json()
                        .then((res) => {
                            const { data, errors } = res;

                            if (errors) {
                                return dispatch(resetPasswordError(errors[0].message));
                            }

                            if (
                                (data || {}).reset_password &&
                                (data || {}).reset_password.success
                            ) {
                                try {
                                    const hint = window.localStorage.getItem("loginHint");
                                    window.localStorage.clear();
                                    window.localStorage.setItem("loginHint", hint);
                                    dispatch(resetPasswordSuccess(data.session));
                                } catch (e) {
                                    console.error(
                                        JSON.stringify(
                                            sanitizeErrorForLogging("100 - Local storage"),
                                        ),
                                    ); // eslint-disable-line no-console
                                }
                                dispatch(logoutSuccess());
                            } else {
                                dispatch(
                                    resetPasswordError(
                                        "Invalid password. Make sure your password is correct.",
                                    ),
                                );
                            }
                        })
                        .catch(() =>
                            dispatch(
                                resetPasswordError(
                                    "Api error or Unknown error. Please try again later",
                                ),
                            ),
                        ),
            );
        }
    };

export const changePassword = (nextCredential, confirmCredential, hash) => {
    return (dispatch) => {
        if (nextCredential !== confirmCredential) {
            return dispatch(
                resetPasswordError('Your "new password" and "confirm password" does not match.'),
            );
        }

        const queriesBody = {
            query: `mutation($p:String!,$n:String!){change_password(newCredential:$p,hash:$n){success}}`,
            variables: {
                p: nextCredential,
                n: hash,
            },
        };

        if (queriesBody) {
            fetchApi("/graphql", queriesBody).then((res) =>
                res
                    .json()
                    .then((res) => {
                        const { data, errors } = res;

                        if (errors) {
                            return dispatch(resetPasswordError(errors[0].message));
                        }

                        if ((data || {}).change_password && (data || {}).change_password.success) {
                            window.location.href = "/login";
                        } else {
                            dispatch(
                                resetPasswordError(
                                    "Invalid password. Make sure your password is correct.",
                                ),
                            );
                        }
                    })
                    .catch(() =>
                        dispatch(
                            resetPasswordError(
                                "Api error or Unknown error. Please try again later",
                            ),
                        ),
                    ),
            );
        }
    };
};

export const handleDownloadFilesAction =
    ({ files, projectId, organizationId }, pushDialog, i18n) =>
    async (dispatch, getState) => {
        for (const file of files) {
            const { id, fileLocation } = file;
            await fetchApi(
                "/api/download_file",
                { dataFileId: id, fileLocation, projectId, organizationId },
                {},
                { dispatch, user: getState()?.user },
            )
                .then((t) => {
                    return t.blob().then((b) => {
                        if (b && b.size > 20) {
                            const a = document.createElement("a");
                            a.href = dompurify.sanitize(URL.createObjectURL(b));
                            let fileBaseName;
                            if (fileLocation) {
                                fileBaseName = fileLocation.substring(
                                    fileLocation.lastIndexOf("/") + 1,
                                );
                            } else {
                                const dotIndex = file.name?.indexOf(".");
                                fileBaseName =
                                    dotIndex >= 0 ? file.name.substring(0, dotIndex) : file.name;
                            }
                            a.setAttribute("download", fileBaseName);
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                        } else {
                            pushDialog("AlertDialog", i18n`Document Download Error`, {
                                title: i18n`Something went wrong when generating the requested file`,
                            }).catch(() => null);
                        }
                    });
                })
                .catch((e) => {
                    throw new Error(e.message);
                });
        }
        return;
    };

export const sendSalesInformationEmail = (projectId, importIds) => (dispatch, getState) => {
    const queriesBody = {
        query: `mutation($p:String!,$i:[Int!]!){send_sales_information_email(projectId:$p,importIds:$i)}`,
        variables: {
            p: projectId,
            i: importIds,
        },
    };
    return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
        .then((res) => res.json().catch(() => ({})))
        .then((res) => {
            const data = res.data || {};
            if (data && data.send_sales_information_email) {
                return true;
            } else {
                throw new Error("Somethings went wrong in sales information submission");
            }
        })
        .catch((e) => {
            dispatch(
                pushDialog("ConfirmDialog", I18n.translate`Submit Error`, {
                    title: I18n.translate`Somethings went wrong in sales information submission`,
                }),
            ).catch(() => null);
            return false;
        });
};

export const upgradeAccount = () => (dispatch, getState) => {
    const queriesBody = {
        query: `mutation{upgrade_account_sso{upgraded, message}}`,
    };
    return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
        .then((res) => res.json().catch(() => ({})))
        .then((res) => {
            const data = res.data["upgrade_account_sso"] || {};
            return data;
        })
        .catch((e) => {
            dispatch(
                pushDialog("AlertDialog", I18n.translate`Submit Error`, {
                    title: I18n.translate`Somethings went wrong during account upgrade`,
                }),
            );
        });
};

export const validateAccountUpgrade = () => (dispatch, getState) => {
    const queriesBody = {
        query: `mutation{validate_account_upgrade{confirmed}}`,
    };
    return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
        .then((res) => res.json().catch(() => ({})))
        .then((res) => {
            const data = res.data["validate_account_upgrade"] || {};
            return data;
        })
        .catch((err) => console.error(JSON.stringify(sanitizeErrorForLogging(err)))); // eslint-disable-line no-console
};

export const checkValidSovosIdentity = (email) => () => {
    const queriesBody = {
        query: `query($e:String!){validate_sovos_identity(email:$e)}`,
        variables: {
            e: email,
        },
    };
    return fetchApi("/graphql", queriesBody)
        .then((res) => res.json().catch(() => ({})))
        .then((res) => {
            const data = res.data["validate_sovos_identity"] || false;
            return data;
        })
        .catch((err) => console.error(JSON.stringify(sanitizeErrorForLogging(err)))); // eslint-disable-line no-console
};

export const getOrganizationDashboardSummary =
    (organizationId, year, yearType) => (dispatch, getState) => {
        const queriesBody = {
            query: `
            query($organizationId: String, $year: String, $yearType: String) {
                organization_dashboard_summary(organizationId: $organizationId, year: $year, yearType: $yearType) {
                    summaries{ summary{key,value} },
                    years{calendaryear, fiscalyear }
                }
            }
        `,
            variables: {
                organizationId: organizationId,
                year: year,
                yearType: yearType,
            },
        };
        return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res.data["organization_dashboard_summary"] || {})
            .catch((err) => console.error(JSON.stringify(sanitizeErrorForLogging(err)))); // eslint-disable-line no-console
    };

export const getRawTablesAvailable =
    (projectId, modulePrefix, fiscalYear) => (dispatch, getState) => {
        const queriesBody = {
            query: `
            query($modulePrefix: String, $fiscalYear: String!, $projectId: String!) {
                tables_available(modulePrefix: $modulePrefix, fiscalYear: $fiscalYear, projectId: $projectId)
            }
        `,
            variables: {
                fiscalYear: fiscalYear,
                modulePrefix: modulePrefix,
                projectId: projectId,
            },
        };

        return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data || [])
            .catch((err) => console.error(JSON.stringify(sanitizeErrorForLogging(err)))); // eslint-disable-line no-console
    };

export const getRawDataModulesWithViewMode = (projectId, fiscalYear) => (dispatch, getState) => {
    const queriesBody = {
        query: `
            query($projectId: String!, $fiscalYear: String!) {
                raw_data_modules_with_entities(projectId: $projectId, fiscalYear: $fiscalYear) {
                    module
                    viewMode
                    entities
                }
            }
        `,
        variables: {
            projectId: projectId,
            fiscalYear: fiscalYear,
        },
    };

    return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
        .then((res) => res.json().catch(() => ({})))
        .then((res) => res?.data?.raw_data_modules_with_entities || [])
        .catch(() => []);
};

export const getCustomCsvTablesAvailable = (projectId, modulePrefix) => (dispatch, getState) => {
    const queriesBody = {
        query: `
            query($modulePrefix: String, $projectId: String!) {
                custom_csv_tables_available(modulePrefix: $modulePrefix, projectId: $projectId) { id name }
            }
        `,
        variables: {
            modulePrefix: modulePrefix,
            projectId: projectId,
        },
    };

    return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
        .then((res) => res.json().catch(() => ({})))
        .then((res) => res?.data || [])
        .catch((err) => console.error(JSON.stringify(sanitizeErrorForLogging(err)))); // eslint-disable-line no-console
};

export const updateRowsAction =
    (projectId, modulePrefix, fiscalYear, importId, entity, updateObject) =>
    (dispatch, getState) => {
        const queriesBody = {
            query: `mutation($p:String!,$fy:String!,$mp:String!,$i:String!,$e:String!,$uObj:String){update_rows_raw_data(projectId:$p,fiscalYear:$fy,modulePrefix:$mp,importId:$i,entity:$e,updateObject:$uObj)}`,
            variables: {
                p: projectId,
                fy: fiscalYear,
                mp: modulePrefix,
                i: importId,
                e: entity,
                uObj: updateObject,
            },
        };

        return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data);
    };

export const updateRowsActionCustomCsv =
    (projectId, entityRawData, updateObject) => (dispatch, getState) => {
        const queriesBody = {
            query: `mutation($p:String!,$e:String!,$uObj:String){update_rows_custom_csv_raw_data(projectId:$p,entity:$e,updateObject:$uObj)}`,
            variables: {
                p: projectId,
                e: entityRawData,
                uObj: updateObject,
            },
        };

        return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data);
    };

export const updateImportProfileAction = (projectId, updateObject) => (dispatch, getState) => {
    const queriesBody = {
        query: `mutation($p:String!,$uObj:String!){update_import_profile(projectId:$p,data:$uObj)}`,
        variables: {
            p: projectId,
            uObj: updateObject,
        },
    };

    return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
        .then((res) => res.json().catch(() => ({})))
        .then((res) => res?.data);
};

export const getImportProfileByIdAction = (projectId, profileId) => (dispatch, getState) =>
    fetchApi(
        "/graphql",
        {
            query: `query($projectId:String!,$filter:[FilterInput!]){
                import_profiles(projectId:$projectId,pageSize:1,page:0,filter:$filter){
                    items { __typename id profile_name profile_headers created_at updated_at has_mapping }
                }
            }`,
            variables: {
                projectId,
                filter: [
                    {
                        column: "ID",
                        operator: "equals",
                        filters: [{ value: String(profileId) }],
                    },
                ],
            },
        },
        {},
        { dispatch, user: getState()?.user },
    )
        .then((res) => res.json().catch(() => ({})))
        .then((res) => res?.data?.import_profiles?.items?.[0] || null)
        .catch((err) => console.error(JSON.stringify(sanitizeErrorForLogging(err)))); // eslint-disable-line no-console

export const getImportProfilesAction = (projectId) => (dispatch, getState) =>
    fetchApi(
        "/graphql",
        {
            query: `query($projectId:String!){
                import_profiles(projectId:$projectId,pageSize:200,page:0){
                    items { id profile_name has_mapping }
                }
            }`,
            variables: { projectId },
        },
        {},
        { dispatch, user: getState()?.user },
    )
        .then((res) => res.json().catch(() => ({})))
        .then((res) => res?.data?.import_profiles?.items || [])
        .catch((err) => {
            console.error(JSON.stringify(sanitizeErrorForLogging(err)));
            return [];
        }); // eslint-disable-line no-console

export const getAvailableMappingContextsAction = (projectId) => (dispatch, getState) =>
    fetchApi(
        "/graphql",
        {
            query: `query($projectId:String!){ available_mapping_contexts(projectId:$projectId){ items{ country_code legal_structures{ id label config_set mappable_entity_count } } total } }`,
            variables: { projectId },
        },
        {},
        { dispatch, user: getState()?.user },
    )
        .then((res) => res.json().catch(() => ({})))
        .then((res) => res?.data?.available_mapping_contexts?.items || [])
        .catch((err) => console.error(JSON.stringify(sanitizeErrorForLogging(err)))); // eslint-disable-line no-console

export const getAvailableEntitiesAction =
    (configSet, legalStructure = null) =>
    (dispatch, getState) =>
        fetchApi(
            "/graphql",
            {
                query: `query($c:String!,$l:String){ available_entities(configSet:$c,legalStructure:$l){ items{ entity_name file_type msch_table cdm_entity_name legal_structure mappable unmappable_reason } } }`,
                variables: { c: configSet, l: legalStructure },
            },
            {},
            { dispatch, user: getState()?.user },
        )
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data?.available_entities?.items || [])
            .catch((err) => console.error(JSON.stringify(sanitizeErrorForLogging(err)))); // eslint-disable-line no-console

export const getAvailableCdmFieldsAction =
    (configSet, entityName, fileType) => (dispatch, getState) =>
        fetchApi(
            "/graphql",
            {
                query: `query($c:String!,$e:String!,$f:String){ available_cdm_fields(configSet:$c,entityName:$e,fileType:$f){ file_type items{ db_name cdm_name field_type nullable } } }`,
                variables: { c: configSet, e: entityName, f: fileType },
            },
            {},
            { dispatch, user: getState()?.user },
        )
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data?.available_cdm_fields || { items: [], file_type: "" })
            .catch((err) => console.error(JSON.stringify(sanitizeErrorForLogging(err)))); // eslint-disable-line no-console

export const createImportProfileMappingAction =
    ({ profileId, projectId, mappingName, configSet, entityName, columnMappings, fileType }) =>
    (dispatch, getState) =>
        fetchApi(
            "/graphql",
            {
                query: `mutation($profileId:Int!,$projectId:String!,$mappingName:String!,$configSet:String!,$entityName:String!,$columnMappings:String!,$fileType:String!){
                    create_import_profile_mapping(profileId:$profileId,projectId:$projectId,mappingName:$mappingName,configSet:$configSet,entityName:$entityName,columnMappings:$columnMappings,fileType:$fileType){ success id message }
                }`,
                variables: {
                    profileId,
                    projectId: String(projectId),
                    mappingName,
                    configSet,
                    entityName,
                    columnMappings,
                    fileType,
                },
            },
            {},
            { dispatch, user: getState()?.user },
        )
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data?.create_import_profile_mapping || { success: false })
            .catch((err) => console.error(JSON.stringify(sanitizeErrorForLogging(err)))); // eslint-disable-line no-console

export const getImportProfileMappingByProfileAction = (profileId) => (dispatch, getState) =>
    fetchApi(
        "/graphql",
        {
            query: `query($profileId:Int!){ import_profile_mappings(profileId:$profileId,pageSize:1,page:0){ items{ id profile_id mapping_name config_set entity_name file_type column_mappings is_active } } }`,
            variables: { profileId },
        },
        {},
        { dispatch, user: getState()?.user },
    )
        .then((res) => res.json().catch(() => ({})))
        .then((res) => res?.data?.import_profile_mappings?.items?.[0] || null)
        .catch((err) => console.error(JSON.stringify(sanitizeErrorForLogging(err)))); // eslint-disable-line no-console

export const updateImportProfileMappingAction =
    (mappingId, projectId, data) => (dispatch, getState) =>
        fetchApi(
            "/graphql",
            {
                query: `mutation($mappingId:Int!,$projectId:String!,$data:String!){ update_import_profile_mapping(mappingId:$mappingId,projectId:$projectId,data:$data){ success id message } }`,
                variables: { mappingId, projectId: String(projectId), data: JSON.stringify(data) },
            },
            {},
            { dispatch, user: getState()?.user },
        )
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data?.update_import_profile_mapping || { success: false })
            .catch((err) => console.error(JSON.stringify(sanitizeErrorForLogging(err)))); // eslint-disable-line no-console

export const deleteImportProfileMappingAction = (mappingId) => (dispatch, getState) =>
    fetchApi(
        "/graphql",
        {
            query: `mutation($mappingId:Int!){ delete_import_profile_mapping(mappingId:$mappingId){ success id message } }`,
            variables: { mappingId },
        },
        {},
        { dispatch, user: getState()?.user },
    )
        .then((res) => res.json().catch(() => ({})))
        .then((res) => res?.data?.delete_import_profile_mapping || { success: false })
        .catch((err) => console.error(JSON.stringify(sanitizeErrorForLogging(err)))); // eslint-disable-line no-console

export const deleteImportProfileAction = (profileId, projectId) => (dispatch, getState) =>
    fetchApi(
        "/graphql",
        {
            query: `mutation($profileId:Int!,$projectId:String!){ delete_import_profile(profileId:$profileId,projectId:$projectId) }`,
            variables: { profileId, projectId: String(projectId) },
        },
        {},
        { dispatch, user: getState()?.user },
    )
        .then((res) => res.json().catch(() => ({})))
        .then((res) => Boolean(res?.data?.delete_import_profile))
        .catch((err) => {
            console.error(JSON.stringify(sanitizeErrorForLogging(err))); // eslint-disable-line no-console
            return false;
        });

export const suggestCustomTemplateEntityAction =
    (profileId, configSet, availableEntities, organizationId) => (dispatch, getState) => {
        const ssoToken = getIdTokenFromStorage() || "";
        const cdmEntityNames = availableEntities
            .map((e) => (typeof e === "string" ? e : e?.cdm_entity_name))
            .filter(Boolean);
        return fetchApi(
            "/graphql",
            {
                query: `query($profileId:Int!,$configSet:String!,$availableEntities:[String!]!,$organizationId:String!,$ssoToken:String!){
                        suggestCustomTemplateEntity(profileId:$profileId,configSet:$configSet,availableEntities:$availableEntities,organizationId:$organizationId,ssoToken:$ssoToken){
                            suggestions{ entity_name score rationale } warnings conversation_id
                        }
                    }`,
                variables: {
                    profileId,
                    configSet,
                    availableEntities: cdmEntityNames,
                    organizationId,
                    ssoToken,
                },
            },
            {},
            { dispatch, user: getState()?.user },
        )
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data?.suggestCustomTemplateEntity || null)
            .catch((err) => {
                console.error(JSON.stringify(sanitizeErrorForLogging(err)));
                return null;
            }); // eslint-disable-line no-console
    };

export const suggestCustomTemplateFiscalYearAction =
    (profileId, availableFiscalYears, organizationId) => (dispatch, getState) => {
        const ssoToken = getIdTokenFromStorage() || "";
        return fetchApi(
            "/graphql",
            {
                query: `query($profileId:Int!,$availableFiscalYears:[String!]!,$organizationId:String!,$ssoToken:String!){
                        suggestCustomTemplateFiscalYear(profileId:$profileId,availableFiscalYears:$availableFiscalYears,organizationId:$organizationId,ssoToken:$ssoToken){
                            fiscal_year score rationale warnings conversation_id
                        }
                    }`,
                variables: {
                    profileId,
                    availableFiscalYears,
                    organizationId,
                    ssoToken,
                },
            },
            {},
            { dispatch, user: getState()?.user },
        )
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data?.suggestCustomTemplateFiscalYear || null)
            .catch((err) => {
                console.error(JSON.stringify(sanitizeErrorForLogging(err)));
                return null;
            });
    };

export const suggestCustomTemplateMappingAction =
    (profileId, configSet, entityName, organizationId, conversationId, fileType) => (dispatch, getState) => {
        const ssoToken = getIdTokenFromStorage() || "";
        return fetchApi(
            "/graphql",
            {
                query: `query($profileId:Int!,$configSet:String!,$entityName:String!,$organizationId:String!,$ssoToken:String!,$conversationId:String,$fileType:String){
                        suggestCustomTemplateMapping(profileId:$profileId,configSet:$configSet,entityName:$entityName,organizationId:$organizationId,ssoToken:$ssoToken,conversationId:$conversationId,fileType:$fileType){
                            entity_name config_set
                            column_mappings{ source_column target_field display_name score rationale }
                            unmapped_source_columns warnings
                        }
                    }`,
                variables: {
                    profileId,
                    configSet,
                    entityName,
                    organizationId,
                    ssoToken,
                    conversationId: conversationId || null,
                    fileType: fileType || null,
                },
            },
            {},
            { dispatch, user: getState()?.user },
        )
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data?.suggestCustomTemplateMapping || null)
            .catch((err) => {
                console.error(JSON.stringify(sanitizeErrorForLogging(err)));
                return null;
            }); // eslint-disable-line no-console
    };

export const suggestDataFixesAction =
    (profileId, projectId, organizationId, columnNames) => (dispatch, getState) => {
        const ssoToken = getIdTokenFromStorage() || "";
        return fetchApi(
            "/graphql",
            {
                query: `query($profileId:Int!,$projectId:Int!,$organizationId:String!,$ssoToken:String!,$columnNames:[String!]){
                        suggestDataFixes(profileId:$profileId,projectId:$projectId,organizationId:$organizationId,ssoToken:$ssoToken,columnNames:$columnNames){
                            entity_name config_set
                            suggestions{
                                column_name issue_type fix_mode rationale confidence
                                total_affected sampled
                                fixes{ clb_id original_value new_value status requires_review skip_reason }
                            }
                            warnings conversation_id
                        }
                    }`,
                variables: {
                    profileId: Number(profileId),
                    projectId: Number(projectId),
                    organizationId,
                    ssoToken,
                    columnNames: columnNames || null,
                },
            },
            {},
            { dispatch, user: getState()?.user },
        )
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data?.suggestDataFixes || null)
            .catch((err) => {
                console.error(JSON.stringify(sanitizeErrorForLogging(err)));
                return null;
            }); // eslint-disable-line no-console
    };

export const acceptDataFixSuggestionAction =
    (profileId, projectId, clbId, column, newValue) => (dispatch, getState) =>
        fetchApi(
            "/graphql",
            {
                query: `mutation($profileId:Int!,$projectId:String!,$clbId:Int!,$column:String!,$newValue:String){
                        acceptDataFixSuggestion(profileId:$profileId,projectId:$projectId,clbId:$clbId,column:$column,newValue:$newValue)
                    }`,
                variables: { profileId, projectId, clbId, column, newValue: newValue ?? null },
            },
            {},
            { dispatch, user: getState()?.user },
        )
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data?.acceptDataFixSuggestion === true)
            .catch((err) => {
                console.error(JSON.stringify(sanitizeErrorForLogging(err)));
                return false;
            }); // eslint-disable-line no-console

export const runCustomTemplateMappingAction =
    ({ profileId, mappingId, projectId, fiscalYear, importId }) =>
    (dispatch, getState) =>
        fetchApi(
            "/graphql",
            {
                query: `mutation($profileId:Int!,$mappingId:Int!,$projectId:String!,$fiscalYear:String!,$importId:Int){
                    run_custom_template_mapping(profileId:$profileId,mappingId:$mappingId,projectId:$projectId,fiscalYear:$fiscalYear,importId:$importId){ success message }
                }`,
                variables: {
                    profileId,
                    mappingId,
                    projectId,
                    fiscalYear,
                    importId: toNumericImportId(importId) ?? null,
                },
            },
            {},
            { dispatch, user: getState()?.user },
        )
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data?.run_custom_template_mapping || { success: false })
            .catch((err) => console.error(JSON.stringify(sanitizeErrorForLogging(err)))); // eslint-disable-line no-console

export const getImportProfileIssuesAction =
    (profileId, mappingId, configSet, entityName, projectId) => (dispatch, getState) =>
        fetchApi(
            "/graphql",
            {
                query: `query($profileId:Int!,$mappingId:Int!,$configSet:String!,$entityName:String!,$projectId:String!){
                        import_profile_issues(profileId:$profileId,mappingId:$mappingId,configSet:$configSet,entityName:$entityName,projectId:$projectId){
                            summary{ total_warnings total_errors affected_rows by_category by_column }
                            rows{ clb_id issues{ column type severity expected actual actual_length } }
                        }
                    }`,
                variables: { profileId, mappingId, configSet, entityName, projectId },
            },
            {},
            { dispatch, user: getState()?.user },
        )
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data?.import_profile_issues || null)
            .catch((err) => {
                console.error(JSON.stringify(sanitizeErrorForLogging(err)));
                return null;
            }); // eslint-disable-line no-console

export const fixImportProfileCellAction =
    (profileId, projectId, clbId, column, value) => (dispatch, getState) =>
        fetchApi(
            "/graphql",
            {
                query: `mutation($profileId:Int!,$projectId:String!,$clbId:Int!,$column:String!,$value:String){
                        fix_import_profile_cell(profileId:$profileId,projectId:$projectId,clbId:$clbId,column:$column,value:$value){
                            success message
                        }
                    }`,
                variables: { profileId, projectId, clbId, column, value: value ?? null },
            },
            {},
            { dispatch, user: getState()?.user },
        )
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data?.fix_import_profile_cell || null)
            .catch((err) => {
                console.error(JSON.stringify(sanitizeErrorForLogging(err)));
                return null;
            }); // eslint-disable-line no-console

export const getLinkedTranslationTablesAction = (profileId, projectId) => (dispatch, getState) =>
    fetchApi(
        "/graphql",
        {
            query: `query($profileId:Int!,$projectId:String!){
                        linked_translation_tables(profileId:$profileId,projectId:$projectId){
                            id name
                        }
                    }`,
            variables: { profileId, projectId },
        },
        {},
        { dispatch, user: getState()?.user },
    )
        .then((res) => res.json().catch(() => ({})))
        .then((res) => res?.data?.linked_translation_tables || [])
        .catch((err) => {
            console.error(JSON.stringify(sanitizeErrorForLogging(err)));
            return [];
        }); // eslint-disable-line no-console

export const getTranslationTablesByImportProfileScopeAction =
    ({ projectId }) =>
    (dispatch, getState) =>
        fetchApi(
            "/graphql",
            {
                query: `query($projectId:Int!){
                        translation_tables_by_import_profile_scope(projectId:$projectId){
                            items { id name description datasetLocale }
                            total
                        }
                    }`,
                variables: { projectId },
            },
            {},
            { dispatch, user: getState()?.user },
        )
            .then((res) => res.json().catch(() => ({})))
            .then(
                (res) =>
                    res?.data?.translation_tables_by_import_profile_scope || {
                        items: [],
                        total: 0,
                    },
            )
            .catch((err) => {
                console.error(JSON.stringify(sanitizeErrorForLogging(err)));
                return { items: [], total: 0 };
            }); // eslint-disable-line no-console

export const getMappingOverviewAction = (projectId, projectModules) => (dispatch, getState) => {
    dispatch({ type: "FETCH_MAPPING_OVERVIEW_REQUEST" });
    return fetchApi(
        "/graphql",
        {
            query: `query($projectId:String!,$projectModules:[String!]!){
                        import_profiles_mapping_overview(projectId:$projectId,projectModules:$projectModules){
                            configSet
                            entities {
                                entityName fileType
                                mandatoryVariants { variant label }
                                profiles {
                                    profileId profileName entityName configSet
                                    totalColumns mappedColumns errorCount warningCount
                                    unmappedMasterFields hasMapping
                                    columns { sourceColumn targetField targetType status }
                                } 
                            }
                        }
                    }`,
            variables: { projectId, projectModules: projectModules || [] },
        },
        {},
        { dispatch, user: getState()?.user },
    )
        .then((res) => res.json().catch(() => ({})))
        .then((res) => {
            const configSets = res?.data?.import_profiles_mapping_overview || [];
            dispatch({ type: "FETCH_MAPPING_OVERVIEW_SUCCESS", payload: configSets });
            return configSets;
        })
        .catch((err) => {
            console.error(JSON.stringify(sanitizeErrorForLogging(err))); // eslint-disable-line no-console
            dispatch({ type: "FETCH_MAPPING_OVERVIEW_FAILURE", error: err.message });
            return [];
        });
};

export const applyTranslatorToStagingAction =
    (profileId, projectId, translationTableId) => (dispatch, getState) =>
        fetchApi(
            "/graphql",
            {
                query: `mutation($profileId:Int!,$projectId:String!,$translationTableId:Int!){
                        apply_translator_to_staging(profileId:$profileId,projectId:$projectId,translationTableId:$translationTableId){
                            message queued status
                        }
                    }`,
                variables: { profileId, projectId, translationTableId },
            },
            {},
            { dispatch, user: getState()?.user },
        )
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data?.apply_translator_to_staging || null)
            .catch((err) => {
                console.error(JSON.stringify(sanitizeErrorForLogging(err)));
                return null;
            }); // eslint-disable-line no-console

export const acceptDataFixesAction = (profileId, projectId, fixes) => (dispatch, getState) =>
    fetchApi(
        "/graphql",
        {
            query: `mutation($profileId:Int!,$projectId:String!,$fixes:[DataFixInput!]!){
                        accept_data_fixes(profileId:$profileId,projectId:$projectId,fixes:$fixes){
                            accepted failed errors
                        }
                    }`,
            variables: { profileId, projectId, fixes },
        },
        {},
        { dispatch, user: getState()?.user },
    )
        .then((res) => res.json().catch(() => ({})))
        .then((res) => res?.data?.accept_data_fixes || null)
        .catch((err) => {
            console.error(JSON.stringify(sanitizeErrorForLogging(err)));
            return null;
        }); // eslint-disable-line no-console

export const triggerIssueDetectionAction =
    (profileId, mappingId, projectId) => (dispatch, getState) =>
        fetchApi(
            "/graphql",
            {
                query: `mutation($profileId:Int!,$mappingId:Int!,$projectId:String!){
                        trigger_issue_detection(profileId:$profileId,mappingId:$mappingId,projectId:$projectId)
                    }`,
                variables: { profileId, mappingId, projectId },
            },
            {},
            { dispatch, user: getState()?.user },
        )
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data?.trigger_issue_detection ?? false)
            .catch((err) => {
                console.error(JSON.stringify(sanitizeErrorForLogging(err)));
                return false;
            }); // eslint-disable-line no-console

export const createProfileTranslationRuleAction =
    (profileId, column, findPattern, replaceValue, name) => (dispatch, getState) =>
        fetchApi(
            "/graphql",
            {
                query: `mutation($profileId:Int!,$column:String!,$findPattern:String!,$replaceValue:String!,$name:String!){
                        create_profile_translation_rule(profileId:$profileId,column:$column,findPattern:$findPattern,replaceValue:$replaceValue,name:$name){
                            id name
                        }
                    }`,
                variables: { profileId, column, findPattern, replaceValue, name },
            },
            {},
            { dispatch, user: getState()?.user },
        )
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data?.create_profile_translation_rule || null)
            .catch((err) => {
                console.error(JSON.stringify(sanitizeErrorForLogging(err)));
                return null;
            }); // eslint-disable-line no-console
